<script setup>
import DsRadioButton from 'JIX/components/ds/DsRadioButton.vue';
import DsCheckbox from 'JIX/components/ds/DsCheckbox.vue';
import Select2 from 'JIX/components/Select2.vue';
import JobsearchDateRange from './JobsearchDateRange.vue';
import JobsearchNearLocation from './JobsearchNearLocation.vue';
import { useStore } from 'vuex';
import { computed, nextTick, ref, watch, onMounted, onBeforeMount, useTemplateRef } from 'vue';
import { __, __x } from 'JIX/gettext.js';
import { mapParam } from 'JIX/jobsearch/utils.js';
import { PART_TIME_MAX_HOURS } from './constants.js';
import debounce from 'lodash/debounce';
import JobsearchGeoAreaField from './JobsearchGeoAreaField.vue';
import $ from 'jquery';

const props = defineProps({
    onlyShowCategories: {
        type: Boolean,
        default: false,
    },
    onlyShowGeography: {
        type: Boolean,
        default: false,
    }
});

const showAll = !props.onlyShowCategories && !props.onlyShowGeography;

const store = useStore();

// They must expose a method called 'clearLocalValue'.
const componentsWithLocalValues = [
    useTemplateRef('nearlocation'),
];

function clearLocalValues() {
    componentsWithLocalValues.forEach(component => component.value?.clearLocalValues());
}

const selectedCategories = computed({
    get: () => store.getters.workingQuery['subid'],
    set: (value) => {
        // Remove unique postfix before storing. This means that
        // selecting a duplicated subcategory always results in selecting
        // the first occurrence.
        value = value.filter(x => !!x).map(s => s.replace(/:.*/, ''));
        store.commit('setParam', { key: 'subid', value });

    },
});

// Subcategories can be present in multiple supercategories, but
// select2 doesn't handle that properly. So make subcategory ids unique by
// postfixing a counter.
const uniqueCategoryOptions = computed(() => {
    const seen = {};
    return store.state.subjobcategory_list
        .map(([superName, subCats]) => {
            return [
                superName,
                subCats.map(([subName, subId]) => {
                    const count = (seen[subId] || 0) + 1;
                    seen[subId] = count;
                    return [
                        subName,
                        count > 1 ? `${subId}:${count}` : subId
                    ];
                })
            ];
        });
});

const selectedEmploymentTypes = mapParam(store, 'employment_type');
const selectedWorkinghoursTypes = mapParam(store, 'workinghours_type');
const selectedEmploymentPlaces = mapParam(store, 'employment_place');
const selectedJobage = mapParam(store, 'jobage');
const selectedAddress = mapParam(store, 'address');
const selectedRadius = mapParam(store, 'radius');
const selectedAreas = mapParam(store, 'geoareaid');
const selectedJobtitles = mapParam(store, 'jobtitleid');
const selectedCompanies = mapParam(store, 'companyid');
const selectedFlags = mapParam(store, 'flag');

const jobageSection = ref(null);

const showPartTimeOptions = computed(() => selectedWorkinghoursTypes.value.includes(store.state.workinghoursPartTimeId));

const workingHoursMinimum = mapParam(store, 'workinghours_min');
const workingHoursMaximum = mapParam(store, 'workinghours_max');
const workingHoursMinimumProxy = ref(workingHoursMinimum.value);
const workingHoursMaximumProxy = ref(workingHoursMaximum.value);

const workingHoursMinError = ref(false);
const workingHoursMaxError = ref(false);
const workingHoursMinExceedsMax = ref(false);

watch([workingHoursMinimumProxy, workingHoursMaximumProxy], debounce(() => {
    workingHoursMinimum.value = workingHoursMinimumProxy.value;
    workingHoursMaximum.value = workingHoursMaximumProxy.value;
    validateWorkingHoursInput();
}, 300));

function validateWorkingHoursInput(){
    workingHoursMinError.value = workingHoursMinimum.value !== null && workingHoursMinimum.value !== ""
    && (workingHoursMinimum.value < 1 || workingHoursMinimum.value > PART_TIME_MAX_HOURS);

    workingHoursMaxError.value = workingHoursMaximum.value !== null && workingHoursMaximum.value !== ""
    && (workingHoursMaximum.value < 1 || workingHoursMaximum.value > PART_TIME_MAX_HOURS);

    workingHoursMinExceedsMax.value = workingHoursMaximum.value && workingHoursMaximum.value < workingHoursMinimum.value;
}

watch(() => selectedJobage.value, async (value) => {
    if (value === 'archive') {
        await scrollToBottomOfJobage();
        store.commit('setDefaultJobageValues');
    } else {
        store.commit('clearJobageValues');
    }
});

const hasJobageError = ref(false);
watch(() => hasJobageError.value, async (value) => {
    if (value) {
        await scrollToBottomOfJobage();
    }
});

async function scrollToBottomOfJobage(){
    await nextTick();
    jobageSection.value.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function onSelectedJobtitle(data){
    store.commit('updateDynamicValueParameterMap', { type:'jobtitleid', value:data.id, text:data.text });
}

function onSelectedCompany(data){
    store.commit('updateDynamicValueParameterMap', { type:'companyid', value:data.id, text:data.text });
}

const jobtitleConfig = ref({
    multiple: true,
    minimumInputLength: 2,
    ajax: {
        url: store.state.findJobtitleUrl,
        dataType: 'json',
        delay: 250,
        data: (params) => {
            return {
                type:"jobtitles",
                q: params.term,
                limit: 10
            };
        },
        processResults: (data) => {
            const res = data.result.map(v => ({ id: v.id, text: v.value }));
            return { results: res };
        }
    },
});

const companyConfig = ref({
    multiple: true,
    minimumInputLength: 2,
    ajax: {
        url: "/api/jobsearch/v3/autocomplete",
        dataType: 'json',
        delay: 250,
        data: (params) => {
            return {
                type:'companyid',
                q: params.term,
                max_results: 10
            };
        },
        processResults: (data) => {
            const results = data.companyid.completions.map(x => {
                return { ...x, id: x.value };
            });
            return { results };
        }
    },
});

const categoryConfig = {
    multiple: true,
    // See https://github.com/select2/select2/issues/3034#issuecomment-338286741 for a discussion on the topic.
    matcher: function(params, data) {
        const defaultMatcher = $.fn.select2.defaults.defaults.matcher;
        const result = defaultMatcher(params, data);
        // Return null if there is no result or if the opt group has no matches.
        if (!result || result.children.length === 0) {
            return null;
        }
        return result;
    }
};

onMounted(() => {
    validateWorkingHoursInput();
});

const initialJobtitleid = ref([]);
const initialCompanyid = ref([]);
onBeforeMount(() => {
    initialJobtitleid.value = [...store.getters.workingQuery.jobtitleid];
    initialCompanyid.value = [...store.getters.workingQuery.companyid];
});

defineExpose({
    clearLocalValues,
});

</script>

<template>
    <div :class="showAll ? 'overflow-auto' : 'w-100'">
        <div class="filter-window__section" v-if="showAll && $store.getters.isAdmin">
            <h5 class="filter-window__section-title">
                Admin
            </h5>
            <div class="filter-window__section-content-vertical">
                <label class="control-label">
                    Flag
                </label>
                <Select2
                    id="flag"
                    name="flag"
                    v-model="selectedFlags"
                    class="form-control"
                    disable-scroll-block
                    multiple
                >
                    <option
                        v-for="flag in $store.state.adminFlags"
                        :key="flag"
                        :value="flag"
                    >
                        {{ flag }}
                    </option>
                </Select2>
            </div>
        </div>
        <div class="filter-window__section" v-if="showAll || onlyShowGeography">
            <h5 class="filter-window__section-title">
                {{ __('Geografi') }}
            </h5>
            <div class="filter-window__section-content-vertical">
                <JobsearchNearLocation
                    ref="nearlocation"
                    v-model:address="selectedAddress"
                    v-model:radius="selectedRadius"
                    :show-title="false"
                />
                <div class="mt-3">
                    <JobsearchGeoAreaField
                        v-model:geo-area-ids="selectedAreas"
                    />
                </div>
            </div>
        </div>
        <div class="filter-window__section filter-window__section--wide" v-if="showAll">
            <h5 class="filter-window__section-title">
                {{ __('Ansættelsestype') }}
            </h5>
            <div class="filter-window__section-content-tiled">
                <div v-for="([type, value]) in store.state.employmentTypes" :key="type">
                    <DsCheckbox
                        :name="type"
                        :value="value"
                        v-model="selectedEmploymentTypes"
                    >
                        {{ type }}
                    </DsCheckbox>
                </div>
            </div>
        </div>
        <div class="filter-window__section" v-if="showAll">
            <h5 class="filter-window__section-title">
                {{ __('Arbejdstid') }}
            </h5>
            <div class="filter-window__section-content-tiled">
                <div v-for="workinghoursType in store.state.workinghoursTypes" :key="workinghoursType[0]">
                    <DsCheckbox
                        name="WorkingHours"
                        :value="workinghoursType[1]"
                        v-model="selectedWorkinghoursTypes"
                    >
                        {{ workinghoursType[0] }}
                    </DsCheckbox>
                </div>
                <div class="d-flex gap-3 w-100" v-if="showPartTimeOptions">
                    <div class="w-100">
                        <label
                            class="control-label"
                            :class="workingHoursMinError || workingHoursMinExceedsMax ? 'text-danger' : ''"
                        >
                            {{ __('Min. timer') }}
                        </label>
                        <input
                            id="minWorkingHours"
                            type="number"
                            v-model.number="workingHoursMinimumProxy"
                            class="form-control"
                            :class="workingHoursMinError || workingHoursMinExceedsMax ? 'error-border-color' : ''"
                        >
                    </div>
                    <div class="w-100">
                        <label
                            class="control-label"
                            :class="workingHoursMaxError || workingHoursMinExceedsMax ? 'text-danger' : ''"
                        >
                            {{ __('Maks. timer') }}
                        </label>
                        <input
                            id="maxWorkingHours"
                            type="number"
                            v-model.number="workingHoursMaximumProxy"
                            class="form-control"
                            :class="workingHoursMaxError || workingHoursMinExceedsMax ? 'error-border-color' : ''"
                        >
                    </div>
                </div>
                <small class="form-text text-danger" v-if="showPartTimeOptions && workingHoursMinExceedsMax">
                    {{ __('Minimum er højere end maksimum') }}
                </small>
                <small class="form-text text-danger" v-else-if="showPartTimeOptions && (workingHoursMinError || workingHoursMaxError)">
                    {{ __x('Angiv mellem 1 og {max} timer', {max: PART_TIME_MAX_HOURS}) }}
                </small>
            </div>
        </div>
        <div class="filter-window__section" v-if="showAll">
            <h5 class="filter-window__section-title">
                {{ __('Hjemmearbejde') }}
            </h5>
            <div class="filter-window__section-content-tiled">
                <div v-for="employmentPlace in store.state.employmentPlaces" :key="employmentPlace[0]">
                    <DsCheckbox
                        :name="employmentPlace[0]"
                        :value="employmentPlace[1]"
                        v-model="selectedEmploymentPlaces"
                    >
                        {{ employmentPlace[0] }}
                    </DsCheckbox>
                </div>
            </div>
        </div>
        <div class="filter-window__section" v-if="showAll && store.state.eligibleForNewJobsearchJobtitles">
            <h5 class="filter-window__section-title">
                {{ __('Jobtitel') }}
            </h5>
            <div class="filter-window__section-content-vertical position-relative">
                <label class="control-label">
                    {{ __('Søg efter jobtitel') }}
                </label>
                <Select2
                    id="jobtitle"
                    name="jobtitleids"
                    v-model="selectedJobtitles"
                    class="form-control"
                    :config="jobtitleConfig"
                    disable-scroll-block
                    multiple
                    @select="onSelectedJobtitle"
                >
                    <option
                        v-for="jobtitleid in initialJobtitleid"
                        :key="jobtitleid"
                        :value="jobtitleid"
                    >
                        {{ store.getters.valueParameterMap.jobtitleid[jobtitleid] }}
                    </option>
                </Select2>
            </div>
        </div>
        <div class="filter-window__section" v-if="showAll || onlyShowCategories">
            <h5 class="filter-window__section-title">
                {{ __('Jobkategori') }}
            </h5>
            <div class="filter-window__section-content-vertical position-relative">
                <label class="control-label">
                    {{ __('Søg efter jobkategori') }}
                </label>
                <Select2
                    id="subjobcategoryid"
                    name="subjobcategoryid"
                    v-model="selectedCategories"
                    disable-scroll-block
                    :config="categoryConfig"
                    class="form-control"
                >
                    <optgroup v-for="([superName, subCats]) in uniqueCategoryOptions" :key="superName" :label="superName">
                        <option
                            v-for="([name, id]) in subCats"
                            :key="id"
                            :value="id"
                        >
                            {{ name }}
                        </option>
                    </optgroup>
                </Select2>
            </div>
        </div>
        <div class="filter-window__section" v-if="showAll">
            <h5 class="filter-window__section-title">
                {{ __('Virksomhed') }}
            </h5>
            <div class="filter-window__section-content-vertical position-relative">
                <label class="control-label">
                    {{ __('Søg efter virksomhed') }}
                </label>
                <Select2
                    id="company"
                    name="companyids"
                    v-model="selectedCompanies"
                    class="form-control"
                    :config="companyConfig"
                    disable-scroll-block
                    multiple
                    @select="onSelectedCompany"
                >
                    <option
                        v-for="companyid in initialCompanyid"
                        :key="companyid"
                        :value="companyid"
                    >
                        {{ store.getters.valueParameterMap.companyid[companyid] }}
                    </option>
                </Select2>
            </div>
        </div>
        <div ref="jobageSection" class="filter-window__section" v-if="showAll">
            <h5 class="filter-window__section-title">
                {{ __('Opslagsdato') }}
            </h5>
            <div class="filter-window__section-content-tiled">
                <div v-for="jobageOption in store.state.jobageOptions" :key="jobageOption.label">
                    <DsRadioButton
                        name="jobage"
                        :value="jobageOption[1]"
                        v-model="selectedJobage"
                    >
                        {{ jobageOption[0] }}
                    </DsRadioButton>
                </div>
                <JobsearchDateRange @has-error="hasJobageError = $event" v-if="selectedJobage === 'archive'" />
            </div>
        </div>
    </div>
</template>
